import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Beaker,
  Droplets,
  FlaskConical,
  Settings,
  FileText,
  AlertCircle,
  Image as ImageIcon,
  CheckCircle2,
  TrendingUp,
  ShoppingBag,
  Clock,
  Edit3
} from 'lucide-react';

/**
 * Mixer Lab - E-Liquid Kalkulátor (Javított verzió)
 * Megoldva: handleBlur és handleBoosterChange hiányzó definíciók.
 */

const App = () => {
  // --- Állapotok ---
  const [recipeName, setRecipeName] = useState("Saját Mixer #1");
  const [totalVolume, setTotalVolume] = useState("120");
  const [aromaVolume, setAromaVolume] = useState("15");
  const [targetNicotine, setTargetNicotine] = useState("3");
  const [boosterNicotine, setBoosterNicotine] = useState("18");
  const [targetVgRatio, setTargetVgRatio] = useState(70);
  const [boosterVgRatio, setBoosterVgRatio] = useState(70);
  const [steepingDays, setSteepingDays] = useState(7);
  const [notes, setNotes] = useState("");

  const canvasRef = useRef(null);

  const [results, setResults] = useState({
    nicotineMl: "0",
    pgMl: "0",
    vgMl: "0",
    aromaMl: "0",
    percentages: { aroma: "0", nic: "0", pg: "0", vg: "0" },
    error: false,
    strengthLabel: "Nikotinmentes",
    bottleCount: 0
  });

  // --- Segédfüggvények ---

  const getNicotineStep = useCallback(() => {
    const totalV = parseFloat(totalVolume) || 120;
    const boostV = parseFloat(boosterNicotine) || 18;
    return parseFloat(((10 * boostV) / totalV).toFixed(2)) || 0.1;
  }, [totalVolume, boosterNicotine]);

  // Anonim logolás a saját szerverre
  const logAction = async (category, action, value = null) => {
    try {
      await fetch('logger.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'stats',
          category,
          action,
          value,
          timestamp: new Date().toISOString()
        })
      });
    } catch (err) { }
  };

  // Recept mentése a szerverre
  const saveRecipeToServer = async (saveType) => {
    const now = new Date();
    const d = now.getFullYear().toString() + (now.getMonth() + 1).toString().padStart(2, '0') + now.getDate().toString().padStart(2, '0');
    const t = now.getHours().toString().padStart(2, '0') + now.getMinutes().toString().padStart(2, '0') + now.getSeconds().toString().padStart(2, '0');
    const r = Math.random().toString(36).substring(2, 6).toUpperCase();
    const recipeId = `${d}_${t}_${r}`;

    const recipeData = {
      type: 'recipe',
      save_id: recipeId,
      save_type: saveType,
      details: {
        name: recipeName,
        total_ml: totalVolume,
        aroma_ml: aromaVolume,
        target_nic: targetNicotine,
        booster_strength: boosterNicotine,
        target_vg: targetVgRatio,
        booster_vg: boosterVgRatio,
        steeping_days: steepingDays,
        notes: notes
      },
      calculated_results: results
    };

    try {
      await fetch('logger.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recipeData)
      });
    } catch (err) { }
  };

  // Oldalbetöltés rögzítése
  useEffect(() => {
    logAction('visits', 'page_load', document.referrer || 'direct');
  }, []);

  // Számítási folyamat
  useEffect(() => {
    const targetNic = parseFloat(targetNicotine) || 0;
    const boostNic = parseFloat(boosterNicotine) || 1;
    const totalVol = parseFloat(totalVolume) || 0;
    const aromaVol = parseFloat(aromaVolume) || 0;

    const nicVolume = (targetNic * totalVol) / boostNic;
    const nicVg = nicVolume * (boosterVgRatio / 100);
    const nicPg = nicVolume * (1 - (boosterVgRatio / 100));
    const totalTargetVg = totalVol * (targetVgRatio / 100);
    const totalTargetPg = totalVol * (1 - (targetVgRatio / 100));

    let addedVg = totalTargetVg - nicVg;
    let addedPg = totalTargetPg - aromaVol - nicPg;

    const isError = addedVg < 0 || addedPg < 0 || (Number(nicVolume) + Number(aromaVol) > totalVol);

    let label = "Nikotinmentes";
    if (targetNic > 0) {
      if (targetNic <= 3.1) label = "Enyhe keverék";
      else if (targetNic <= 6.1) label = "Közepes keverék";
      else if (targetNic <= 12.1) label = "Erős keverék";
      else label = "Extra erős keverék";
    }

    setResults({
      nicotineMl: Math.max(0, nicVolume).toFixed(1),
      pgMl: Math.max(0, addedPg).toFixed(1),
      vgMl: Math.max(0, addedVg).toFixed(1),
      aromaMl: aromaVol.toFixed(1),
      percentages: {
        aroma: ((aromaVol / totalVol) * 100 || 0).toFixed(1),
        nic: ((nicVolume / totalVol) * 100 || 0).toFixed(1),
        pg: (Math.max(0, addedPg) / totalVol * 100 || 0).toFixed(1),
        vg: (Math.max(0, addedVg) / totalVol * 100 || 0).toFixed(1)
      },
      error: isError,
      strengthLabel: label,
      bottleCount: Math.round(nicVolume / 10)
    });
  }, [totalVolume, aromaVolume, targetNicotine, boosterNicotine, targetVgRatio, boosterVgRatio]);

  // --- Eseménykezelők ---

  const handleInputChange = (setter, value, logName) => {
    if (value === "") { setter(""); return; }
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setter(value);
      if (logName) logAction('inputs', logName, value);
    }
  };

  const handleBlur = (setter, value, fallback = "0") => {
    if (value === "" || isNaN(parseFloat(value))) {
      setter(fallback);
    }
  };

  const handleTotalVolumeChange = (val) => {
    logAction('inputs', 'bottle_size_change', val);
    if (val === "") { setTotalVolume(""); return; }
    const newVol = parseFloat(val);
    if (isNaN(newVol) || newVol < 0) return;
    const oldVol = parseFloat(totalVolume) || 1;
    const oldNic = parseFloat(targetNicotine) || 0;
    const bNic = parseFloat(boosterNicotine) || 18;
    const nicVol = (oldNic * oldVol) / bNic;
    const currentBottleCount = Math.round(nicVol / 10) || 0;
    setTotalVolume(val);
    if (currentBottleCount > 0 && newVol > 0) {
      const newTargetNic = (currentBottleCount * 10 * bNic) / newVol;
      setTargetNicotine(newTargetNic.toFixed(2));
    }
  };

  const handleBoosterChange = (val) => {
    logAction('inputs', 'booster_mg_change', val);
    const newBoosterNic = parseFloat(val) || 0;
    const currentNicVolume = (parseFloat(targetNicotine) * parseFloat(totalVolume)) / (parseFloat(boosterNicotine) || 1);
    const currentBottleCount = Math.round(currentNicVolume / 10) || 1;
    setBoosterNicotine(val);
    const newTargetNic = (currentBottleCount * 10 * newBoosterNic) / (parseFloat(totalVolume) || 120);
    setTargetNicotine(newTargetNic.toFixed(2));
  };

  const downloadImage = () => {
    logAction('buttons', 'download_png', recipeName);
    saveRecipeToServer('png');
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = 1000; canvas.height = 1150;
    const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    bgGradient.addColorStop(0, '#f8fafc'); bgGradient.addColorStop(1, '#f1f5f9');
    ctx.fillStyle = bgGradient; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#4f46e5'; ctx.fillRect(0, 0, canvas.width, 180);
    ctx.fillStyle = '#ffffff'; ctx.font = 'bold 50px sans-serif'; ctx.fillText('MIXER LAB RECEPT', 60, 90);
    ctx.font = '24px sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText(recipeName.toUpperCase(), 60, 130); ctx.fillText(new Date().toLocaleDateString('hu-HU'), 750, 130);

    const drawRoundedRect = (x, y, w, h, r, fill) => {
      ctx.beginPath(); ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h); ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r); ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath();
      ctx.fillStyle = fill; ctx.fill();
    };
    drawRoundedRect(40, 220, 920, 880, 50, '#ffffff');

    const drawStat = (x, y, label, val, color) => {
      ctx.fillStyle = '#94a3b8'; ctx.font = 'bold 16px sans-serif'; ctx.fillText(label, x, y);
      ctx.fillStyle = color; ctx.font = 'bold 36px sans-serif'; ctx.fillText(val, x, y + 45);
    };
    drawStat(80, 300, 'ÖSSZ TÉRFOGAT', `${totalVolume}ml`, '#1e293b');
    drawStat(350, 300, 'NIKOTIN SZINT', `${targetNicotine}mg`, '#ef4444');
    drawStat(650, 300, 'CÉL ARÁNY', `${targetVgRatio}/${100 - targetVgRatio} VG/PG`, '#10b981');
    drawStat(80, 420, 'BOOSTER IGÉNY', `${results.bottleCount} db`, '#f59e0b');
    drawStat(350, 420, 'BOOSTER ERŐSSÉG', `${boosterNicotine} mg/ml`, '#6366f1');

    const bX = 700, bY = 420, bW = 160, bH = 370;
    const bottlePath = () => {
      ctx.beginPath(); ctx.moveTo(bX + 50, bY); ctx.lineTo(bX + 110, bY); ctx.lineTo(bX + 120, bY + 30);
      ctx.lineTo(bX + 160, bY + 70); ctx.lineTo(bX + 160, bY + bH - 20);
      ctx.quadraticCurveTo(bX + 160, bY + bH, bX + 140, bY + bH); ctx.lineTo(bX + 20, bY + bH);
      ctx.quadraticCurveTo(bX, bY + bH, bX, bY + bH - 20); ctx.lineTo(bX, bY + 70);
      ctx.lineTo(bX + 40, bY + 30); ctx.closePath();
    };
    ctx.save(); bottlePath(); ctx.clip();
    const p = results.percentages;
    let curY = bY + bH;
    const drawL = (pct, color) => {
      const h = (parseFloat(pct) / 100) * bH; ctx.fillStyle = color; ctx.fillRect(bX, curY - h, bW, h); curY -= h;
    };
    drawL(p.vg, '#10b981'); drawL(p.pg, '#3b82f6'); drawL(p.nic, '#ef4444'); drawL(p.aroma, '#f59e0b');
    ctx.restore(); ctx.strokeStyle = '#e2e8f0'; ctx.lineWidth = 4; bottlePath(); ctx.stroke();

    const drawIng = (y, label, val, pct, color) => {
      ctx.fillStyle = color; ctx.beginPath(); ctx.arc(80, y - 10, 10, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#475569'; ctx.font = 'bold 22px sans-serif'; ctx.fillText(`${label} (${pct}%)`, 110, y);
      ctx.fillStyle = '#1e293b'; ctx.font = 'bold 28px sans-serif'; ctx.fillText(`${val} ml`, 450, y);
    };
    drawIng(550, 'Aroma', results.aromaMl, p.aroma, '#f59e0b');
    drawIng(630, 'Booster', results.nicotineMl, p.nic, '#ef4444');
    drawIng(710, 'Alap PG', results.pgMl, p.pg, '#3b82f6');
    drawIng(790, 'Alap VG', results.vgMl, p.vg, '#10b981');

    ctx.fillStyle = '#f8fafc'; ctx.fillRect(80, 880, 840, 180);
    ctx.fillStyle = '#64748b'; ctx.font = 'italic 20px sans-serif'; ctx.fillText('JEGYZETEK:', 100, 920);
    ctx.fillStyle = '#1e293b'; ctx.font = '20px sans-serif';
    const steepingText = `Érlelési idő: ${steepingDays} nap. `;
    const fullNotes = steepingText + (notes || "");
    ctx.fillText(fullNotes.substring(0, 85), 100, 960);
    if (fullNotes.length > 85) ctx.fillText(fullNotes.substring(85, 170), 100, 990);

    const link = document.createElement('a');
    link.download = `RECEPT_${recipeName.replace(/\s+/g, '_')}.png`;
    link.href = canvas.toDataURL('image/png', 1.0);
    link.click();
  };

  const downloadTxt = () => {
    logAction('buttons', 'download_txt', recipeName);
    saveRecipeToServer('txt');
    const content = `MIXER LAB RECEPT: ${recipeName}\n----------------\nDátum: ${new Date().toLocaleDateString('hu-HU')}\nÖssz mennyiség: ${totalVolume} ml\nCél nikotin: ${targetNicotine} mg/ml\nBooster igény: ${results.bottleCount} db (${boosterNicotine} mg/ml)\nÉrlelési idő: ${steepingDays} nap\n\nÖSSZETEVŐK:\nAroma: ${results.aromaMl} ml (${results.percentages.aroma}%)\nBooster: ${results.nicotineMl} ml (${results.percentages.nic}%)\nAlap PG: ${results.pgMl} ml (${results.percentages.pg}%)\nAlap VG: ${results.vgMl} ml (${results.percentages.vg}%)\n\nJEGYZETEK:\n${notes || "Nincs"}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `${recipeName.replace(/\s+/g, '_')}.txt`; link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 p-4 md:p-8 font-sans selection:bg-indigo-100">
      <div className="max-w-6xl mx-auto">
        <canvas ref={canvasRef} className="hidden" />
        <header className="mb-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-indigo-600 p-3 rounded-2xl shadow-xl rotate-3 transition-all hover:rotate-0">
              <FlaskConical className="text-white w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-slate-900 tracking-tight">Mixer Lab</h1>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Precíziós Mixer</p>
              </div>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <div className="flex flex-col">
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 p-8 h-full flex flex-col">
              <div className="flex items-center gap-3 mb-5 border-b border-slate-100 pb-2 flex-shrink-0">
                <Settings className="w-6 h-6 text-indigo-600" />
                <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wider">Paraméterek</h2>
              </div>

              <div className="space-y-6 flex-1">
                <div className="space-y-1.5">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Recept Neve</label>
                  <input type="text" value={recipeName} onChange={(e) => setRecipeName(e.target.value)} onBlur={() => logAction('inputs', 'recipe_name_blur')} className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-slate-700 transition-all border-transparent focus:border-indigo-500 shadow-inner" />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">Flakon Méret</label>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-2">
                    {[60, 80, 100, 120].map(size => (
                      <button key={size} onClick={() => handleTotalVolumeChange(String(size))} className={`py-3.5 rounded-xl font-black text-[11px] sm:text-xs transition-all ${parseFloat(totalVolume) === size ? 'bg-indigo-600 text-white shadow-lg scale-105 z-10' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        {size} ml
                      </button>
                    ))}
                    <div className="col-span-2 md:col-span-1 flex items-center justify-between gap-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1.5 shadow-inner focus-within:ring-4 focus-within:ring-indigo-500/15 transition-all min-h-[46px]">
                      <div className="flex flex-col text-[7px] md:text-[8px] font-black text-slate-400 uppercase leading-tight min-w-[30px] lg:hidden">
                        <span>Egyedi</span><span>méret</span>
                      </div>
                      <input type="number" min="0" value={totalVolume} onChange={(e) => handleTotalVolumeChange(e.target.value)} onBlur={() => handleBlur(setTotalVolume, totalVolume, "120")} className="w-full bg-transparent font-black text-right lg:text-center outline-none text-slate-700 text-xs" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-end px-1">
                    <label className="text-xs font-black text-slate-400 uppercase tracking-widest text-[11px]">Aroma Mennyiség</label>
                    <div className="flex items-center gap-2">
                      <input type="number" step="0.1" value={aromaVolume} onChange={(e) => handleInputChange(setAromaVolume, e.target.value, 'aroma_ml')} onBlur={() => handleBlur(setAromaVolume, aromaVolume, "0")} className="w-20 bg-amber-50 border border-amber-200 rounded-xl px-2 py-1.5 text-center font-black text-amber-700 outline-none shadow-sm" />
                      <span className="text-xs font-black text-amber-600/50 uppercase">ml</span>
                    </div>
                  </div>
                  <input type="range" min="0" max={parseFloat(totalVolume) || 120} step="0.5" value={parseFloat(aromaVolume) || 0} onChange={(e) => handleInputChange(setAromaVolume, e.target.value, 'aroma_slider')} className="w-full h-3 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-amber-500" />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-400 uppercase tracking-widest px-1 text-[11px]">Booster Arány (VG/PG)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[70, 50].map(ratio => (
                      <button key={ratio} onClick={() => { setBoosterVgRatio(ratio); logAction('inputs', `booster_ratio_${ratio}`); }} className={`py-2.5 rounded-2xl font-black text-xs transition-all ${boosterVgRatio === ratio ? 'bg-indigo-600 text-white shadow-md scale-105' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        {ratio}/{100 - ratio}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-black text-red-600/70 uppercase tracking-widest px-1 leading-tight text-[11px]">Cél Nikotin (mg)</label>
                    <input type="number" step={getNicotineStep()} value={targetNicotine} onChange={(e) => handleInputChange(setTargetNicotine, e.target.value, 'target_nicotine')} onBlur={() => handleBlur(setTargetNicotine, targetNicotine, "0")} className="w-full bg-red-50/30 border border-red-100 rounded-2xl px-4 py-3 font-black text-red-600 outline-none shadow-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-black text-indigo-400 uppercase tracking-widest px-1 leading-tight text-[11px]">Booster (mg/ml)</label>
                    <input type="number" step="0.1" value={boosterNicotine} onChange={(e) => handleBoosterChange(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-black text-slate-700 outline-none shadow-sm" />
                  </div>
                </div>

                <div className="space-y-2 pb-1">
                  <label className="text-xs font-black text-emerald-600/70 uppercase tracking-widest px-1 text-[11px]">Cél VG Tartalom</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[50, 60, 70, 80].map(val => (
                      <button key={val} onClick={() => { setTargetVgRatio(val); logAction('inputs', `target_vg_${val}`); }} className={`py-2.5 rounded-2xl font-black text-xs transition-all ${targetVgRatio === val ? 'bg-emerald-600 text-white shadow-md scale-105' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        {val}/{100 - val}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100 mt-1">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 px-1 text-[11px]">Saját megjegyzések</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div className="flex flex-col justify-center space-y-2 py-1">
                      <div className="flex justify-between items-center px-1">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 leading-none"><Clock className="w-3 h-3" /> Érlelés</label>
                        <span className="text-xs font-black text-indigo-600 leading-none">{steepingDays} nap</span>
                      </div>
                      <input type="range" min="0" max={30} value={steepingDays} onChange={(e) => { setSteepingDays(parseInt(e.target.value)); logAction('inputs', 'steeping_slider'); }} className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-indigo-500" />
                    </div>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => logAction('inputs', 'notes_blur')} placeholder="Márka, íz..." className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-medium outline-none h-14 resize-none shadow-inner" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col h-full overflow-hidden">
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 p-8 h-full flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
              <div className="flex items-center gap-3 mb-5 border-b border-slate-100 pb-2 flex-shrink-0">
                <Beaker className="w-6 h-6 text-emerald-600" />
                <h2 className="text-xl font-bold text-slate-800 uppercase tracking-wider text-center flex-1">Keverési Recept</h2>
              </div>

              <div className="flex flex-col md:flex-row gap-10 items-center mb-8 flex-shrink-0 justify-center">
                <div className="relative w-28 h-60 flex-shrink-0">
                  <svg viewBox="0 0 100 200" className="w-full h-full relative z-10 drop-shadow-2xl">
                    <defs><clipPath id="bClip"><path d="M30 5 L70 5 L75 25 L85 45 L85 185 Q85 195 70 195 L30 195 Q15 195 15 185 L15 45 L25 25 Z" /></clipPath></defs>
                    <path d="M30 5 L70 5 L75 25 L85 45 L85 185 Q85 195 70 195 L30 195 Q15 195 15 185 L15 45 L25 25 Z" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2" />
                    <g clipPath="url(#bClip)">
                      <rect x="0" y={200 - (parseFloat(results.percentages.vg) * 2)} width="100" height="200" fill="#10b981" fillOpacity="0.8" />
                      <rect x="0" y={200 - ((parseFloat(results.percentages.vg) + parseFloat(results.percentages.pg)) * 2)} width="100" height={parseFloat(results.percentages.pg) * 2} fill="#3b82f6" fillOpacity="0.8" />
                      <rect x="0" y={200 - ((parseFloat(results.percentages.vg) + parseFloat(results.percentages.pg) + parseFloat(results.percentages.nic)) * 2)} width="100" height={parseFloat(results.percentages.nic) * 2} fill="#ef4444" fillOpacity="0.8" />
                      <rect x="0" y={200 - ((parseFloat(results.percentages.vg) + parseFloat(results.percentages.pg) + parseFloat(results.percentages.nic) + parseFloat(results.percentages.aroma)) * 2)} width="100" height={parseFloat(results.percentages.aroma) * 2} fill="#f59e0b" fillOpacity="0.8" />
                    </g>
                  </svg>
                </div>
                <div className="flex-1 w-full space-y-3 max-w-sm">
                  <IngredientRow label="Aroma" value={results.aromaMl} pct={results.percentages.aroma} color="amber" />
                  <IngredientRow label="Booster" value={results.nicotineMl} pct={results.percentages.nic} color="red" />
                  <IngredientRow label="Alap PG" value={results.pgMl} pct={results.percentages.pg} color="blue" />
                  <IngredientRow label="Alap VG" value={results.vgMl} pct={results.percentages.vg} color="emerald" />
                </div>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center mb-6 w-full px-2 min-h-[160px]">
                {results.error ? (
                  <div className="rounded-3xl p-6 bg-red-50 border border-red-100 flex items-center gap-5 text-red-600 animate-pulse shadow-sm w-full mx-auto max-w-sm h-full flex justify-center items-center">
                    <div className="p-3.5 bg-red-100 rounded-2xl flex-shrink-0"><AlertCircle className="w-8 h-8" /></div>
                    <div><p className="text-sm font-black uppercase">Helytelen adatok!</p><p className="text-[11px] font-bold text-red-500 uppercase mt-1">Túllépték a flakon kapacitását.</p></div>
                  </div>
                ) : (
                  <div className="space-y-4 w-full max-w-sm">
                    <div className="rounded-3xl p-5 bg-emerald-50 border border-emerald-100 flex items-center gap-4 text-emerald-700 shadow-sm w-full">
                      <div className="p-3 bg-emerald-100 rounded-2xl"><TrendingUp className="w-6 h-6" /></div>
                      <div><p className="text-xs font-black uppercase tracking-widest">{String(results.strengthLabel)}</p><p className="text-[10px] font-bold text-emerald-600/80 uppercase mt-1">A keverés adatai megfelelőek.</p></div>
                    </div>
                    <div className="rounded-3xl p-5 bg-amber-50 border border-amber-100 flex items-center gap-4 text-amber-700 shadow-sm w-full">
                      <div className="p-3 bg-amber-100 rounded-2xl text-amber-600"><ShoppingBag className="w-6 h-6" /></div>
                      <div><p className="text-xs font-black uppercase tracking-widest">Nikotin szükséglet</p><p className="text-xl font-black mt-1">{String(results.bottleCount)} <span className="text-[10px] font-bold opacity-60 uppercase">db booster (10ml)</span></p></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-6 border-t border-slate-100 flex justify-between items-end bg-white/50 -mx-8 px-8 pb-4 flex-shrink-0">
                <div><p className="text-[10px] font-black text-slate-400 uppercase mb-1">Kész Mennyiség</p><p className="text-5xl font-black text-slate-900 tracking-tighter">{String(totalVolume)}<span className="text-xl text-slate-400 ml-2 font-bold">ml</span></p></div>
                <div className="text-right"><p className="text-[10px] font-black text-slate-400 uppercase mb-1">Végleges Arány</p><p className="text-2xl font-black text-emerald-600">{targetVgRatio}/{100 - targetVgRatio}</p></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 flex-shrink-0">
                <button onClick={downloadImage} className="group bg-indigo-600 text-white py-4 px-6 rounded-2xl font-black shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-3 active:scale-95"><ImageIcon className="w-5 h-5" /> <span className="text-sm uppercase">PNG Mentés</span></button>
                <button onClick={downloadTxt} className="group bg-white border-2 border-slate-200 text-slate-500 py-4 px-6 rounded-2xl font-black hover:border-indigo-200 transition-all flex items-center justify-center gap-3 active:scale-95"><FileText className="w-5 h-5" /> <span className="text-sm uppercase">TXT Mentés</span></button>
              </div>
            </div>
          </div>
        </div>

        <footer className="mt-16 text-center">
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.4em] mb-2 opacity-50">Mixer Lab Precision Mixer</p>
          <div className="h-1 w-20 bg-indigo-500/20 mx-auto rounded-full"></div>
        </footer>
      </div>
    </div>
  );
};

const IngredientRow = ({ label, value, pct, color }) => {
  const colorMap = { amber: "bg-amber-500", red: "bg-red-500", blue: "bg-blue-500", emerald: "bg-emerald-500" };
  const bgMap = { amber: "bg-amber-50 text-amber-700", red: "bg-red-50 text-red-700", blue: "bg-blue-50 text-blue-700", emerald: "bg-emerald-50 text-emerald-700" };
  return (
    <div className={`flex items-center justify-between p-3.5 rounded-2xl border border-transparent transition-all bg-white shadow-sm ${bgMap[color] || ""}`}>
      <div className="flex flex-col"><div className="flex items-center gap-3"><div className={`w-2.5 h-2.5 rounded-full ${colorMap[color] || "bg-slate-400"} shadow-sm`}></div><span className="text-[11px] font-black uppercase">{String(label)}</span></div><span className="text-[9px] font-bold opacity-60 ml-5 uppercase mt-1">{String(pct)}% rész</span></div>
      <div className="text-right"><span className="text-xl font-black">{String(value)}</span><span className="text-[10px] font-bold opacity-50 ml-1 uppercase">ml</span></div>
    </div>
  );
};

export default App;